import { useCallback, useEffect, useRef } from 'react';
import { useUnit } from 'effector-react';
import { button, useControls } from 'leva';
import type { Schema } from 'leva/dist/declarations/src/types/public.js';
import type { CollisionStateChangedEvent } from '@datum-sdk/engine';
import { cartesianToSpherical } from '@datum-sdk/helpers';
import { $splatDevHandles } from '../../state/splatDevTools';
import { $devConfig, resetAppConfig, updateDevConfig } from '../../state/appConfig';
import { $locale, setLocale } from '../../state/locale';
import type { Locale } from '../../shared/locale';
import { useSplatColliders } from './useSplatColliders';
import { useSightCameraSync } from './useSightCameraSync';
import { useNavBeaconTuner } from './useNavBeaconTuner';
import { useAppControls } from './useAppControls';
import { useViewOffsetControls } from './useViewOffsetControls';
import { copyAppConfig } from './appConfigSnapshot';

type ChangeContext = { initial?: boolean };

/**
 * Compact dev panel for splat scenes (`?dev`), built on
 * the SDK's own tooling where it exists (collider debug views, runtime
 * collider/POI/appearance APIs) instead of porting the legacy DevVisuals. The
 * route editor is its own panel (`?flight`, `FlightEditorPanel`).
 * Renders null — only Leva folders.
 * All live handles come from `$splatDevHandles` (published by createSdkScene in
 * dev mode only — the store is null in production and the panel goes inert).
 */
export const SplatDevPanel = () => {
  const handles = useUnit($splatDevHandles);
  const locale = useUnit($locale);
  const engine = handles?.engine ?? null;

  // ── 1. Language ──────────────────────────────────────────────────────────
  useControls(
    'Localization',
    {
      Language: {
        options: { English: 'en', Arabic: 'ar' },
        value: locale,
        onChange: (v: Locale, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || v === $locale.getState()) return;
          setLocale(v);
        },
      },
    },
    [locale],
  );

  // ── App-wide settings (sound, assistant token, mocks, preloader skip) ─────
  useAppControls();

  // ── 2. Camera state (realtime) — pivot in `Orbit Center` (first), pose in
  // `Camera`. Leva orders folders by useControls call order.
  const [, setOrbitCenter] = useControls(
    'Orbit Center',
    () => ({
      tgtX: { value: 0, label: 'X', disabled: true },
      tgtY: { value: 0, label: 'Y', disabled: true },
      tgtZ: { value: 0, label: 'Z', disabled: true },
      'Copy Orbit Center': button(() => {
        const eng = $splatDevHandles.getState()?.engine;
        if (!eng) return;
        const s = eng.getCameraState();
        const r = (n: number) => +n.toFixed(3);
        const json = `[${s.lookAt.map(r).join(', ')}]`;
        navigator.clipboard?.writeText(json);
        console.log(`[SplatDevPanel] Orbit center: ${json}`);
      }),
    }),
    [],
  );

  const [, setCamera] = useControls(
    'Camera',
    () => ({
      posX: { value: 0, label: 'Pos X', disabled: true },
      posY: { value: 0, label: 'Pos Y', disabled: true },
      posZ: { value: 0, label: 'Pos Z', disabled: true },
      distance: { value: 0, label: 'Distance', disabled: true },
      polar: { value: 0, label: 'Polar (rad)', disabled: true },
      azimuth: { value: 0, label: 'Azimuth (rad)', disabled: true },
      fov: { value: 0, label: 'FOV', disabled: true },
      aspect: { value: 0, label: 'Aspect', disabled: true },
      near: { value: 0, label: 'Near', disabled: true },
      far: { value: 0, label: 'Far', disabled: true },
      'Copy Pose': button(() => {
        const eng = $splatDevHandles.getState()?.engine;
        if (!eng) return;
        const s = eng.getCameraState();
        const r = (n: number) => +n.toFixed(3);
        const json = `position: [${s.position.map(r).join(', ')}],\nlookAt: [${s.lookAt.map(r).join(', ')}],`;
        navigator.clipboard?.writeText(json);
        console.log(`[SplatDevPanel] Pose:\n${json}`);
      }),
    }),
    [],
  );

  const cameraPrevRef = useRef('');
  const orbitPrevRef = useRef('');
  useEffect(() => {
    if (!engine) return;
    let raf = 0;
    const tick = () => {
      const s = engine.getCameraState();
      const sph = cartesianToSpherical(
        [s.lookAt[0], s.lookAt[1], s.lookAt[2]],
        [s.position[0], s.position[1], s.position[2]],
      );
      const r = (n: number) => +n.toFixed(3);
      const cam = {
        posX: r(s.position[0]),
        posY: r(s.position[1]),
        posZ: r(s.position[2]),
        distance: r(sph.radius),
        polar: r(sph.polar),
        azimuth: r(sph.azimuth),
      };
      const camKey = JSON.stringify(cam);
      if (camKey !== cameraPrevRef.current) {
        cameraPrevRef.current = camKey;
        setCamera(cam);
      }
      const orbit = { tgtX: r(s.lookAt[0]), tgtY: r(s.lookAt[1]), tgtZ: r(s.lookAt[2]) };
      const orbitKey = JSON.stringify(orbit);
      if (orbitKey !== orbitPrevRef.current) {
        orbitPrevRef.current = orbitKey;
        setOrbitCenter(orbit);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [engine, setCamera, setOrbitCenter]);

  // Projection as the engine drew the last frame (`?camera-debug` on legacy).
  const projectionPrevRef = useRef('');
  useEffect(() => {
    if (!engine) return;
    return engine.subscribeFrameRendered((frame) => {
      const projection = {
        fov: +frame.fov.toFixed(1),
        aspect: +frame.aspect.toFixed(2),
        near: +frame.near.toFixed(2),
        far: +frame.far.toFixed(1),
      };
      const key = JSON.stringify(projection);
      if (key === projectionPrevRef.current) return;
      projectionPrevRef.current = key;
      setCamera(projection);
    });
  }, [engine, setCamera]);

  // ── View Offset (the chat panel's covered edges, by hand) ────────────────
  useViewOffsetControls();

  // ── 4. Highlight: colliders (the engine's collider debug views) ──────────
  // A view is built per collider on demand and freed on toggle-off; a removed
  // collider frees its own. Views die with the engine (scene change).
  const highlightCollidersRef = useRef(false);

  const applyColliderHighlight = useCallback(
    (on: boolean) => {
      highlightCollidersRef.current = on;
      if (!engine) return;
      for (const id of engine.getColliderIds()) {
        if (on) engine.ensureColliderDebugView(id);
        else engine.releaseColliderDebugView(id);
      }
    },
    [engine],
  );

  // A fresh collider has no view yet: re-apply after a panel add (procedural
  // shapes register synchronously) and whenever the scene's colliders settle —
  // the id list can still be filling in when the toggle is flipped.
  const restyleAfterMutation = useCallback(() => {
    if (highlightCollidersRef.current) applyColliderHighlight(true);
  }, [applyColliderHighlight]);

  useEffect(() => {
    if (!engine) return;
    const onCollisionState = ({ curState }: CollisionStateChangedEvent): void => {
      if (curState === 'ready') restyleAfterMutation();
    };
    engine.on('collision:state-changed', onCollisionState);
    return () => {
      engine.off('collision:state-changed', onCollisionState);
      highlightCollidersRef.current = false;
    };
  }, [engine, restyleAfterMutation]);

  useControls(
    'Highlight',
    {
      'Show Colliders': {
        value: false,
        onChange: (v: boolean, _p: string, ctx: ChangeContext) => {
          if (ctx.initial) return;
          applyColliderHighlight(v);
        },
      },
      // Persisted and shared with the legacy panel; the scene applies it live.
      'Show Orbit Center': {
        value: $devConfig.getState().showOrbitCenterMarker,
        hint: 'Маркер точки, вокруг которой вращается камера (DebugOverlay SDK).',
        onChange: (v: boolean, _p: string, ctx: ChangeContext) => {
          if (ctx.initial) return;
          updateDevConfig({ showOrbitCenterMarker: v });
        },
      },
    },
    [engine],
  );

  // ── 5 + 6. Preloader duration + dev toggles (persisted devConfig) ────────
  // Schema is built ONCE (empty deps, initial values from getState) — deps on
  // the live $devConfig would make every onChange rebuild the folder MID-DRAG
  // and break the slider (values snapping back under the cursor).
  useControls(
    'Preloader & Camera',
    () => {
      const dev = $devConfig.getState();
      return {
        'Preloader Hold (s)': {
          value: dev.preloaderHoldSec,
          min: 0,
          max: 1000,
          step: 1,
          hint: 'Задержка после загрузки сплата: вертушка прелоадера крутится N сек до съезда/reveal/флайовера. Следующий вход в сцену.',
          onChange: (v: number, _p: string, ctx: ChangeContext) => {
            if (ctx.initial) return;
            updateDevConfig({ preloaderHoldSec: v });
          },
        },
        'Disable Forced View Mode': {
          value: dev.disableForcedViewMode,
          hint: 'FPS-сцены (mina, inside) в orbit для тюнинга. Requires reload.',
          onChange: (v: boolean, _p: string, ctx: ChangeContext) => {
            if (ctx.initial) return;
            updateDevConfig({ disableForcedViewMode: v });
          },
        },
        'Unlock Camera In Preloader': {
          value: dev.unlockCameraDuringPreloader,
          hint: 'Без тур-синематика/блокера; reveal играет, камера свободна. Requires reload.',
          onChange: (v: boolean, _p: string, ctx: ChangeContext) => {
            if (ctx.initial) return;
            updateDevConfig({ unlockCameraDuringPreloader: v });
          },
        },
        'Unlock FPS Movement': {
          value: dev.unlockFpsMovement,
          hint: 'WASD/скролл/пинч на look-only FPS-сценах. Requires reload.',
          onChange: (v: boolean, _p: string, ctx: ChangeContext) => {
            if (ctx.initial) return;
            updateDevConfig({ unlockFpsMovement: v });
          },
        },
        'Unlock Camera Constraints': {
          value: dev.unlockCameraConstraints,
          hint: 'ЖИВОЙ: снимает pitch-констрейнт и распахивает orbit-лимиты. Y ограничен containment-коллайдером.',
          onChange: (v: boolean, _p: string, ctx: ChangeContext) => {
            if (ctx.initial) return;
            updateDevConfig({ unlockCameraConstraints: v });
          },
        },
      };
    },
    [],
  );

  // ── Render Settings (live tuning of the CURRENT device/tier) ─────────────
  // Seeded from the adapter-computed constructor settings; every edit pushes
  // the FULL current set through `setRuntimeOptions({ renderSettings })` —
  // the runtime layer wins the SDK merge chain, applies live.
  const renderSettingsRef = useRef<Record<string, number>>({});
  useEffect(() => {
    renderSettingsRef.current = { ...(handles?.renderSettings ?? {}) };
  }, [handles]);

  const buildRenderSchema = useCallback((): Schema => {
    const rs = handles?.renderSettings;
    if (!rs) return {};
    const field = (key: string, min: number, max: number, step: number) => ({
      value: rs[key] ?? 0,
      min,
      max,
      step,
      label: key,
      onChange: (v: number, _p: string, ctx: ChangeContext) => {
        if (ctx.initial) return;
        renderSettingsRef.current[key] = v;
        $splatDevHandles.getState()?.engine.setRuntimeOptions({
          renderSettings: { ...renderSettingsRef.current },
        });
      },
    });
    return {
      maxPixelRatio: field('maxPixelRatio', 0.5, 3, 0.05),
      motionPixelRatioScale: field('motionPixelRatioScale', 0.3, 1, 0.01),
      minSortIntervalMs: field('minSortIntervalMs', 0, 100, 1),
      blurAmount: field('blurAmount', 0, 1, 0.01),
      preBlurAmount: field('preBlurAmount', 0, 1, 0.01),
      maxStdDev: field('maxStdDev', 0.5, 4, 0.01),
      minPixelRadius: field('minPixelRadius', 0, 8, 0.1),
      maxPixelRadius: field('maxPixelRadius', 1, 512, 1),
      minAlpha: field('minAlpha', 0, 0.1, 0.001),
      clipXY: field('clipXY', 1, 2, 0.05),
      falloff: field('falloff', 0, 2, 0.05),
      focalAdjustment: field('focalAdjustment', 0.5, 2, 0.05),
      'Copy Settings': button(() => {
        const json = JSON.stringify(renderSettingsRef.current, null, 2);
        navigator.clipboard?.writeText(json);
        console.log(`[SplatDevPanel] Render settings:\n${json}`);
      }),
    };
  }, [handles]);
  useControls('Render Settings', buildRenderSchema, [buildRenderSchema]);

  // ── 3. Colliders ─────────────────────────────────────────────────────────
  const colliders = useSplatColliders(engine, restyleAfterMutation);
  useControls('Colliders', colliders.buildSchema, colliders.schemaDeps);

  // ── 7. Sight Camera Config ───────────────────────────────────────────────
  const sight = useSightCameraSync(handles);
  const [, sightSet, sightGet] = useControls('Sight Camera Config', sight.buildSchema, sight.schemaDeps);
  sight.captureSetGet(sightSet, sightGet);

  // ── Nav Beacons (`overall`; empty elsewhere) ─────────────────────────────
  const navBeacons = useNavBeaconTuner(handles);
  useControls('Nav Beacons', navBeacons.buildSchema, navBeacons.schemaDeps);

  // ── Storage escape hatch (legacy parity — $appConfig persists on every
  // keystroke; a stray persisted toggle/scale survives reloads forever) ─────
  useControls('Storage', {
    // Reloads: SDK scenes read most dev settings at mount, and the blocks above
    // are built once from the store.
    'Reset to Defaults & Reload': button(() => {
      resetAppConfig();
      window.location.reload();
    }),
    'Copy App Config': button(copyAppConfig),
    'Clear Storage & Reload': button(() => {
      localStorage.clear();
      window.location.reload();
    }),
  });

  return null;
};
